import {
  Alert,
  AlertAction,
  AlertDescription,
  AlertTitle,
} from "@postdaily/ui/components/alert";
import { Button } from "@postdaily/ui/components/button";
import { Spinner } from "@postdaily/ui/components/spinner";
import {
  createFileRoute,
  Navigate,
  Outlet,
  useRouterState,
} from "@tanstack/react-router";
import { useState } from "react";

import {
  WorkspaceGate,
  WorkspaceProvider,
} from "@/hooks/use-current-workspace";
import { authClient } from "@/lib/auth-client";
import { createNoIndexHead } from "@/lib/seo";

export const Route = createFileRoute("/(connect)")({
  // Consent is signed-in and per-request: nothing to render on the server.
  ssr: false,
  head: () => createNoIndexHead("Connect an app — PostDaily"),
  component: RouteComponent,
});

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-svh items-center justify-center bg-background p-4">
      {children}
    </div>
  );
}

/**
 * Layout for pages another app sends a person to — today only the AI-agent
 * consent screen. Signed out, it goes to `/login` and comes back to the same
 * URL; a brand-new account gets its default workspace from the provider
 * before the page asks which workspaces to share.
 */
function RouteComponent() {
  const href = useRouterState({ select: (state) => state.location.href });
  // Captured once. While the redirect below is in flight this layout renders
  // again with the URL already at /login, and reading it live nested
  // `/login?redirect=/login?redirect=…` until React gave up — the sign-in then
  // rejected that (auth-surface) redirect and dropped the person on /queue.
  const [returnTo] = useState(href);
  const { data: session, isPending, error, refetch } = authClient.useSession();

  if (isPending) {
    return (
      <Centered>
        <Spinner className="size-10" />
      </Centered>
    );
  }

  if (error) {
    return (
      <Centered>
        <Alert variant="destructive" className="max-w-md">
          <AlertTitle>Could not check your session</AlertTitle>
          <AlertDescription>
            We could not confirm your sign-in state. Try again to continue.
          </AlertDescription>
          <AlertAction>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => {
                void refetch();
              }}
            >
              Try again
            </Button>
          </AlertAction>
        </Alert>
      </Centered>
    );
  }

  if (!session) {
    return <Navigate to="/login" search={{ redirect: returnTo }} replace />;
  }

  return (
    <WorkspaceProvider>
      <WorkspaceGate>
        <Outlet />
      </WorkspaceGate>
    </WorkspaceProvider>
  );
}
